import { queryStream } from '@/features/workspaces/stream';
import { DOCUMENT_TYPE, type DocumentTypeName } from '@/features/documents/constants';
import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { isAxiosError } from 'axios';
import { ARTIFACTS_QUERY_KEY } from '@/features/artifacts/queries';
import { entityDocumentsQuery, indexingStatusQuery } from '@/features/documents/queries';
import { getIndexingState } from '@/features/documents/utils';
import { invalidateScopeConsumers } from '@/features/subcontractor-bids/queries';
import type { ScopeAgentStatus } from './types';
import { isScopeAgentRunActive, scopeRunEnded } from './utils';

export const scopeAgentStatusQuery = (resourceId: string, type: DocumentTypeName) => {
  const queryKey = ['scopeAgent', resourceId];
  const refreshConsumers = (client: QueryClient, next: ScopeAgentStatus) => {
    if (scopeRunEnded(client.getQueryData<ScopeAgentStatus>(queryKey), next)) {
      invalidateScopeConsumers(client, resourceId);
      client.invalidateQueries({ queryKey: [ARTIFACTS_QUERY_KEY] });
    }
  };
  return queryOptions({
    queryKey,
    queryFn: async ({ client }) => {
      try {
        const { data: status } = await api.get<ScopeAgentStatus>('v1/work-packages/scope-agent', {
          params: { tenderId: resourceId },
        });
        refreshConsumers(client, status);
        return status;
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === 404 &&
          error.response.data?.detail === 'Not Found'
        )
          return {
            evaluationId: resourceId,
            artifactId: null,
            run: null,
            knowledgeBases: [],
            hasTenderBill: false,
            available: false,
          } satisfies ScopeAgentStatus;
        throw error;
      }
    },
    refetchInterval: (query) => (isScopeAgentRunActive(query.state.data) ? 30_000 : false),
    meta: {
      stream: queryStream<ScopeAgentStatus, ScopeAgentStatus | { entity: string }>({
        topic: 'scope_breakdown',
        type: DOCUMENT_TYPE[type],
        resourceId,
        isActive: isScopeAgentRunActive,
        onStatusUpdate: (payload, client) => {
          if (!('evaluationId' in payload)) return client.invalidateQueries({ queryKey });
          refreshConsumers(client, payload);
          client.setQueryData(queryKey, payload);
        },
      }),
    },
  });
};

export function useScopeBreakdownPreparation(resourceId: string, type: DocumentTypeName) {
  const documents = useQuery(entityDocumentsQuery(resourceId, type));
  const hasDocuments = documents.isSuccess && documents.data.length > 0;
  const source = useQuery({
    ...indexingStatusQuery(type, resourceId),
    enabled: hasDocuments,
  });
  return documents.isSuccess && !hasDocuments
    ? 'missing'
    : documents.isError || source.isError
      ? 'failed'
      : source.isPending
        ? 'loading'
        : hasDocuments && getIndexingState(source.data).isIndexed
          ? source.data?.indexedPercentage === 0
            ? 'unavailable'
            : 'ready'
          : 'preparing';
}
